import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Storage } from '@ionic/storage-angular';

export interface Locale {
  code: string;
  name: string;
  lang: string;
  /** Shown on the language screen, next to the name. */
  flag: string;
}

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  public readonly locales: Locale[] = [
    { code: 'fr-FR', name: 'USER_PROFILE.LANGUAGE.FRENCH', lang: 'fr', flag: '🇫🇷' },
    { code: 'en-US', name: 'USER_PROFILE.LANGUAGE.ENGLISH', lang: 'en', flag: '🇬🇧' },
  ];
  private storage: Storage = inject(Storage);
  public get locale(): Locale {
    return this._locale ?? this.locales[0];
  }

  private readonly LANGUAGE_KEY = 'preferred_language';
  private _locale: Locale = this.locales[0];

  constructor(private translate: TranslateService) {}

  public async initStorage() {
    await this.storage.create();
    const savedLocale = await this.storage.get(this.LANGUAGE_KEY);
    if (savedLocale) {
      this.setLocales(savedLocale);
    } else {
      // Get device language
      const deviceLang = this.getDeviceLocale();
      this.setLocales(deviceLang.code);
    }
  }

  getCurrentLang(): string {
    return this.locale.lang;
  }

  async setLocales(localeKey: string) {
    let locale = this.locales.find((l) => l.code === localeKey);
    if (!locale) {
      locale = this.locales[0];
    }
    this.translate.use(locale.lang);
    this._locale = locale;
    await this.storage.set(this.LANGUAGE_KEY, localeKey);
  }

  private getDeviceLocale(): Locale {
    // Look for the exact match
    let locale = this.locales.find((l) => l.code === navigator.language);
    if (!locale) {
      // Look for the language match
      locale = this.locales.find((l) => l.lang === navigator.language.split('-')[0]);
    }
    if (!locale) {
      // If no match, use the first locale
      locale = this.locales[0];
    }
    return locale;
  }
}
